<template>
  <div>
    <select
      v-show="instance === null"
      :ref="initRef"
      v-model="model"
      :disabled="disabled"
      :multiple="multiple"
    />
  </div>
</template>
<script setup>
/*

Just select tag w/ typeahead support,
model is a primitive value as defined in "keys" prop

*/

import { computed, inject, onUnmounted, ref, shallowRef, toValue, watch } from 'vue'
import Choices from 'choices.js'
import { useThrottleFn } from '@vueuse/core'

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  keys: {
    type: String,
    default: 'id',
  },
  values: {
    type: String,
    default: 'name',
  },
  shouldSelect: {
    type: Function,
    default: x => Number(x),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autoSort: {
    type: Boolean,
    default: true,
  },
  multiple: Boolean,
  searchEnabled: {
    type: Boolean,
    default: true,
  },
  addOptions: Boolean,
})
const model = defineModel()
const emit = defineEmits(['search', 'add-option', 'link-selected'])
const instance = ref(null)
const onSearch = useThrottleFn(evt => emit('search', evt.detail.value), 1000)
const initRef = el => {
  if (!instance.value) {
    instance.value = new Choices(el, {
      allowHTML: true,
      shouldSort: props.autoSort,
      noResultsText: 'Ничего не найдено',
      itemSelectText: '',
      resetScrollPosition: false,
      searchEnabled: props.searchEnabled,
      searchResultLimit: -1,
      searchFields: [props.values],
      addItems: props.addOptions,
      addChoices: props.addOptions,
      fuseOptions: {
        shouldSort: true,
        findAllMatches: true,
        ignoreLocation: true,
        threshold: 0.4,
      },
      callbackOnCreateTemplates() {
        return {
          choice: (instance, data) => {
            const option = Choices.defaults.templates.choice.call(this, instance, data)

            if (data.customProperties?.emits === true) {
              option.classList.add('link-primary', 'text-decoration-underline')
            }

            return option
          },
        }
      },
    })
    if (props.searchEnabled) {
      el.addEventListener('search', onSearch)
    }
    if (props.addOptions) {
      el.addEventListener('addItem', onAddItem)
    }
    el.addEventListener('choice', onChoice)
    setall()
  }
}

const formEditable = inject('form:editable', () => shallowRef(true), true)
const disabled = computed(() => formEditable.value === false || props.disabled)
onUnmounted(() => {
  instance.value.destroy()
  if (props.searchEnabled) {
    instance.value.passedElement.element.removeEventListener('search', onSearch)
  }
  if (props.addOptions) {
    instance.value.passedElement.element.removeEventListener('addItem', onAddItem)
  }
  instance.value.passedElement.element.removeEventListener('choice', onChoice)
})

watch(() => props.options, setall, { immediate: true })
watch(model, setall, { immediate: true })
watch(
  () => [formEditable.value, props.disabled],
  ([a, b]) => {
    if (!instance.value) {
      return
    }

    if (!a || b) {
      instance.value.disable()
    } else {
      instance.value.enable()
    }
  },
  { immediate: true }
)

function setall() {
  if (!instance.value) {
    return
  }
  let options = [...props.options]

  if (!options.length && model.value) {
    if (props.multiple) {
      options = [...options, ...model.value]
    } else {
      options.push({ ...model.value })
    }
  }
  instance.value.clearStore()
  instance.value.setChoices(options, props.keys, props.values, true)
  instance.value.highlightAll()

  let mv = toValue(model.value)
  instance.value.removeActiveItems()

  const ss = props.shouldSelect(mv, options)
  if (ss && options.length) {
    instance.value.setChoiceByValue(ss)
    return
  }
}

function onAddItem(evt) {
  emit('add-option', evt.detail.value)
}

function onChoice({ detail }) {
  if (detail.customProperties?.emits === true) {
    emit('link-selected', detail)
    // reset model value
    setall()
  }
}
</script>
